import { useRef } from 'react';
import { TextField } from '@mui/material';
import { type SxProps, type Theme } from '@mui/material/styles';

/**
 * Props interface for CustomTextInput component
 */
export interface ICustomTextInputProps {
  /** Current value of the input field */
  value: string;
  /** Label text displayed above the input field */
  label: string;
  /** Name attribute for the input field */
  name: string;
  /** Whether the field is required (shows asterisk) */
  required?: boolean;
  /** Whether the input should take full width of its container */
  fullWidth?: boolean;
  /** Callback function called when input value changes */
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  /** Helper text displayed below the input field */
  helperText?: string;
  /** Custom styles to apply to the TextField */
  sx?: SxProps<Theme> | undefined;
  /** Whether the input should automatically receive focus */
  autoFocus?: boolean;
  /** Whether this is the first child (used for autofocus workaround) */
  isFirstChild?: boolean;
  /** Type of input field (text, password, email, etc.) */
  type?: string;
  /** Additional props to pass to the input element */
  inputProps?: unknown;
  /** Whether the input field is disabled */
  disabled?: boolean;
}

/**
 * Custom text input component built on top of Material-UI TextField
 * Provides consistent styling and behavior across the application
 *
 * @param props - Component props
 * @returns JSX element representing the text input field
 */
const CustomTextInput = (props: ICustomTextInputProps): JSX.Element => {
  const {
    value,
    label,
    name,
    required,
    fullWidth,
    onChange,
    helperText,
    sx,
    autoFocus,
    isFirstChild,
    type = 'text',
    inputProps,
    disabled,
  } = props;

  /**
   * Reference to the input element for autofocus functionality
   * There is a bug in MUI in which autofocus sometimes doesn't
   * happen in React strict mode
   * https://github.com/mui/material-ui/issues/33004
   */
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <TextField
      type={type}
      inputRef={inputRef}
      autoFocus={isFirstChild ? autoFocus : false}
      margin="normal"
      required={required ?? false}
      fullWidth={fullWidth ?? true}
      label={label}
      name={name}
      value={value}
      onChange={onChange}
      sx={
        sx ?? {
          '& p': {
            color: 'red',
          },
          mb: 2,
        }
      }
      helperText={helperText}
      inputProps={inputProps}
      disabled={disabled}
    />
  );
};

export default CustomTextInput;
// import { useRef, useEffect } from 'react';
// import { TextField } from '@mui/material';
// import { type SxProps, type Theme } from '@mui/material/styles';

// export interface ICustomTextInputProps {
//   value: string;
//   label: string;
//   name: string;
//   required?: boolean;
//   fullWidth?: boolean;
//   onChange: (
//     e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
//   ) => void;
//   helperText?: string;
//   sx?: SxProps<Theme> | undefined;
//   autoFocus?: boolean;
// }

// const CustomTextInput = (props: ICustomTextInputProps): JSX.Element => {
//   const {
//     value,
//     label,
//     name,
//     required,
//     fullWidth,
//     onChange,
//     helperText,
//     sx,
//     autoFocus,
//   } = props;

//   const inputRef = useRef<HTMLInputElement>(null);

//   /**
//    * there is a bug in mui in which autofocus sometimes doesn't
//    * happen in react strict mode
//    * https://github.com/mui/material-ui/issues/33004
//    */

//   useEffect(() => {
//     if (inputRef.current) {
//       inputRef.current.focus();
//     }
//   }, []);

//   return (
//     <TextField
//       inputRef={inputRef}
//       autoFocus={autoFocus ?? false}
//       margin="normal"
//       required={required ?? false}
//       fullWidth={fullWidth ?? true}
//       label={label}
//       name={name}
//       value={value}
//       onChange={onChange}
//       sx={
//         sx ?? {
//           '& p': {
//             color: 'red',
//           },
//           mb: 2,
//         }
//       }
//       helperText={helperText}
//     />
//   );
// };

// export default CustomTextInput;
